
import { Injectable, Injector } from '@angular/core';

import { Subject } from 'rxjs/internal/Subject';
import { Observable } from 'rxjs/internal/Observable';
import {BehaviorSubject} from 'rxjs/internal/BehaviorSubject';

import { User } from '../models/user';

import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// import * as jwt_decode from 'jwt-decode';
import jwt_decode, { JwtPayload } from "jwt-decode";

export const TOKEN_NAME: string = 'jwt_token';

import { UserIdleService } from 'angular-user-idle';
import { SharedUtilsService } from './shared-utils.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUserLoggedIn;
  public loggedInUser$ = new BehaviorSubject<User | null>(null);

  private headerOptions = {headers: new HttpHeaders({'Content-Type': 'application/json'})};

  private userIdleSet: boolean = false;

  public userPreEmulateKey = 'user-pre-emulate';

  public redirectUrl: string; // Holds the site url that user is trying to access when logged out so they can be sent there after login.

  private timeoutTimer = {
    hasLoadedTimeoutDialog: false,
    countdownValue: 0,
    dialogRef: null
  }

  constructor(
    private userIdle: UserIdleService,
    private httpClient: HttpClient,
    private _sharedUtilsService: SharedUtilsService,
    private router: Router,
    private injector: Injector
  ) {
    const user = this.loginStatus;
    this.loggedInUser$.next(user);
  }


  // Getter for loggedInUser$ BehaviorSubject
  get loginStatus(): User | null {
    try {
      const storedUserObject = this._sharedUtilsService.getFromLocal('currentUser');
      const parsedObject = (storedUserObject != undefined) ? JSON.parse(storedUserObject) : null;
      const userInStorage: User = parsedObject?.user ?? null;
      return userInStorage;
    } catch (_err) {
      console.error('Failed to get login status:', _err);
      return null;
    }
  }


  /* ---------- JWT Attempt ---------- */
  getToken(): string {
    // check date set. If it was not today, then remove token from local storage
    return localStorage.getItem(TOKEN_NAME);
  }


  setToken(token: string): void {
    // set date set
    // const authStorageToken = {authToken: token, timestamp: new Date().getTime()};
    // console.log('Set storage auth item to: ', token);
    
    localStorage.setItem(TOKEN_NAME, token);
  }


  getTokenExpirationDate(token: string): Date {
    const decoded = jwt_decode<JwtPayload>(token);

    if (decoded.exp === undefined) return null;

    const date = new Date(0); 
    date.setUTCSeconds(decoded.exp);
    return date;
  }


  isTokenExpired(token?: string): boolean {
    // console.log('Checking if token is expired: ', token);

    if(!token) token = this.getToken();
    if(!token) return true;

    const tokenExpDate = this.getTokenExpirationDate(token);

    if(tokenExpDate === undefined) return false;

    return tokenExpDate.valueOf() < new Date().valueOf();
  }
  /* --------- END JWT Attempt --------- */


  async initializeTimeoutDialog() {
    let currentUser = this.getCurrentlyLoggedInUser();
    if (currentUser && currentUser.email === 'jweber@eduhealthcare.com') {
      this.timeoutTimer.hasLoadedTimeoutDialog = false;
      this.userIdle.resetTimer();
      return;
    }

    this.timeoutTimer.hasLoadedTimeoutDialog = true;

    const [{ MatDialog }, { TimeoutDialogComponent }] = await Promise.all([
      import('@angular/material/dialog'),
      import('@app/components/shared/dialogs/timeout-dialog/timeout-dialog.component'),
    ]);

    const dialogRef = this.injector.get(MatDialog).open(TimeoutDialogComponent);
    this.timeoutTimer.dialogRef = dialogRef;
    const instance = dialogRef.componentInstance;
    instance.secondsUntilTimeout = this.timeoutTimer.countdownValue - 2; // give it a bit of a head start

    dialogRef.afterClosed().subscribe(_usersChoiceToContinue => {
      if (_usersChoiceToContinue) {
        this.timeoutTimer.hasLoadedTimeoutDialog = false;
        this.userIdle.resetTimer();
      } else {
        this.userIdle.stopWatching();
        this.clearItemsInLocalStorage();
        this.router.navigate(['/logout']);
      }
    });
  }


  setUserIdle() {
    // prevent multiple subscribes, etc.
    if (this.userIdleSet) return;

    this.userIdleSet = true;

    this.userIdle.stopWatching();
    this.userIdle.startWatching();

    // Start watching when user idle is starting.
    this.userIdle.onTimerStart().subscribe(count => {
      const timeOut = this.userIdle.getConfigValue().timeout;

      // open a dialog and show the countdown along with a Continue Session button to close it and continue working...
      if (count === 1 && !this.timeoutTimer.hasLoadedTimeoutDialog) this.initializeTimeoutDialog();

      this.timeoutTimer.countdownValue = timeOut - count;
    });

    // Start watch when time is up.
    this.userIdle.onTimeout().subscribe(() => {
      this.userIdle.stopWatching();
      this.resetUserIdleValues();
      this.clearItemsInLocalStorage();
      this._sharedUtilsService.showNotification('You have been logged out due to inactivity.');

      this.router.navigate(['/logout']);
    });
  }


  stopUserIdle() {
    this.userIdle.stopTimer();
    this.userIdle.stopWatching();
  }


  resetUserIdleValues() {
    this.userIdleSet = false; // Need to set false incase user logs back in but refresh did not happen. Will not set idle if still true.
    this.timeoutTimer.hasLoadedTimeoutDialog = false; // This needs to be reset incase window is not refreshed to get dialog to show next time idle is set.
  }


  clearItemsInLocalStorage() {
    this._sharedUtilsService.clearKeyFromLocal(this.userPreEmulateKey);
    this._sharedUtilsService.clearKeyFromLocal('currentUser');
    this._sharedUtilsService.clearKeyFromLocal('jwt_token');
  }


  logout() {
    this.resetUserIdleValues();
    this.clearItemsInLocalStorage();
    this.currentUserLoggedIn = null;
    this.loggedInUser$.next(null); // Setting BehaviorSubject to null, this will make sure to tell every subscriber about the change.
  }


  checkForAdminPrivileges(_user: User) {
    if (!_user || _user === undefined || !_user.userType || _user.userType === undefined) return false;
    return _user.userType === 'System Admin';
  }


  getCurrentlyLoggedInUser(): User {
    let currentUser = this.currentUserLoggedIn;

    if (currentUser) {
      this.setUserIdle();
      this.userIdle.resetTimer();
      return currentUser;
    }

    const ufs = this._sharedUtilsService.getFromLocal('currentUser');
    // console.log('User from storage: ', ufs);

    if (ufs) {
      currentUser = JSON.parse(ufs).user;
      this.currentUserLoggedIn = currentUser;
      this.setUserIdle();
      this.userIdle.resetTimer();
      // console.log('Found it in storage: ', this.currentUserLoggedIn);
    }

    return currentUser;
  }


  setCurrentlyLoggedInUser(_usr: User) {
    this.currentUserLoggedIn = _usr;
    const token = this.getToken();

    const newStorageItem = {
      auth: true,
      error: null,
      status: true,
      token: token,
      user: _usr
    };

    // console.log('New Storage Item: ', newStorageItem);

    this.loggedInUser$.next(_usr); // Setting BehaviorSubject to passed user, this will make sure to tell every subscriber about the change.
    this._sharedUtilsService.saveInLocal('currentUser', JSON.stringify(newStorageItem));
  }


  doesUserHaveAccessForGivenType(typeNeededForRoute: string) {
    // Check if there is a user logged in and if it has the matching userType
    const storedUserObject = this._sharedUtilsService.getFromLocal('currentUser');
    const userInStorage: User = (storedUserObject && storedUserObject !== undefined && JSON.parse(storedUserObject).user) ? JSON.parse(storedUserObject).user : null;

    if (userInStorage && userInStorage !== undefined) {
      if (userInStorage.userType === typeNeededForRoute) return true;
      else return false;
    } else return false;
  }
}
